/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import { animated, useTransition } from '@react-spring/web';
import React, {
    FunctionComponent,
    ReactNode,
    Ref,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { WrappedLink } from '@toucan/client/global/nextjs';

import {
    Link,
    LinkColor,
    LinkSize,
} from '@toucan/client/shared/components/link';
import { useClickOutside } from '@toucan/client/shared/hooks/use-click-outside';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';

import { useBlurWithin } from './hooks/use-blur-within';
import { useFocusWithin } from './hooks/use-focus-within';
import { styles } from './popout-menu.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface PopoutMenuItem {
    /**
     * Text to show in the link
     */
    text: string;

    /**
     * The url of the link
     */
    url?: string;

    /**
     * An on click event for the link
     */
    onClick?: () => void;

    /**
     * An optional template path for use in NextJS routing
     */
    templatePath?: string;
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
enum PopoutMenuInternalOpenState {
    OPENING = 'opening',
    CLOSING = 'closing',
    IDLE = 'idle',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface PopoutMenuProps {
    /**
     * If the component is open
     */
    isOpen: boolean;

    /**
     * Title of the menu
     */
    menuTitle?: string;

    /**
     * The items to show in the menu
     */
    menuItems: PopoutMenuItem[];

    /**
     * A prefix used for any ids that are generated
     */
    idPrefix?: string;

    /**
     * A function used to generate a trigger element
     *
     * @param id {string} The id to assign to a aria-controls attribute
     * @param triggerRef {Ref<HTMLElement>} A ref prop to assign to the component
     */
    trigger: (id: string, triggerRef: Ref<HTMLElement>) => ReactNode;

    /**
     * Handler for when the component should open
     */
    onOpen?: () => void;

    /**
     * Handler for when the component should close
     */
    onClose?: () => void;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledPopoutMenu = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const PopoutMenu: FunctionComponent<PopoutMenuProps> = ({
    isOpen,
    onOpen,
    onClose,
    trigger,
    menuTitle,
    menuItems,
    idPrefix = 'popout-menu',
    ...otherProps
}: PopoutMenuProps) => {
    const menuWrapperID = `${idPrefix}-menu-wrapper`;
    const menuTitleID = `${idPrefix}-menu-title`;
    const menuListID = `${idPrefix}-menu`;

    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement>(null);
    const menuWrapperRef = useRef<HTMLDivElement>(null);

    const [svgPath, setSvgPath] = useState('');
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isMouseInside, setIsMouseInside] = useState(false);

    const [internalOpenState, setInternalOpenState] =
        useState<PopoutMenuInternalOpenState>(PopoutMenuInternalOpenState.IDLE);

    const navProps = {
        className: 'menu',
    };

    /**
     * Handles setting internal state when the mouseenter event fires
     */
    const handleMouseEnter = () => {
        setIsMouseInside(true);
        if (!isOpen) {
            setInternalOpenState(PopoutMenuInternalOpenState.OPENING);
        }
    };

    /**
     * Handles setting internal state when the mouseleave event fires
     */
    const handleMouseLeave = () => {
        setIsMouseInside(false);
        if (
            isOpen &&
            (document.activeElement !== triggerRef.current ||
                activeIndex === null)
        ) {
            setInternalOpenState(PopoutMenuInternalOpenState.CLOSING);
        }
    };

    const handleClickOutside = () => {
        setIsMouseInside(false);
        if (isOpen) {
            setInternalOpenState(PopoutMenuInternalOpenState.CLOSING);
        }
    };

    const handleClick = () => {
        setIsMouseInside(true);
        if (!isOpen) {
            setInternalOpenState(PopoutMenuInternalOpenState.OPENING);
        }
    };

    useClickOutside([rootRef], handleClickOutside);

    useBlurWithin(rootRef, (event) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const skipActiveIndexCheck = event.target
            ? (event.target as HTMLElement).matches('.menu-link')
            : false;

        if ((activeIndex === null || skipActiveIndexCheck) && !isMouseInside) {
            setInternalOpenState(PopoutMenuInternalOpenState.CLOSING);
        }
    });

    useFocusWithin(rootRef, () => {
        setInternalOpenState(PopoutMenuInternalOpenState.OPENING);
    });

    useKeyPress(rootRef.current, ['Tab'], (event: KeyboardEvent) => {
        setActiveIndex(null);

        // When holding shift key focus returns to trigger element
        if (!event.shiftKey && !isMouseInside) {
            setInternalOpenState(PopoutMenuInternalOpenState.CLOSING);
        } else if (
            event.shiftKey &&
            document.activeElement !== triggerRef.current
        ) {
            // Only wanna set state to opening when shift tabbing from an item in the menu
            setInternalOpenState(PopoutMenuInternalOpenState.OPENING);
        }
    });

    useKeyPress(
        rootRef.current,
        ['ArrowDown', 'Down'],
        (event: KeyboardEvent) => {
            if (!isOpen) {
                return;
            }

            event.preventDefault();

            const newIndex =
                activeIndex !== null ? (activeIndex + 1) % menuItems.length : 0;

            setActiveIndex(newIndex);
        }
    );

    useKeyPress(rootRef.current, ['ArrowUp', 'Up'], (event: KeyboardEvent) => {
        if (!isOpen) return;
        event.preventDefault();

        const newIndex =
            activeIndex !== null
                ? (activeIndex - 1 + menuItems.length) % menuItems.length
                : 0;

        setActiveIndex(newIndex);
    });

    /**
     * Focuses the correct link when activeIndex changes
     */
    useEffect(() => {
        const menuList = document.querySelector(`#${menuListID}`);
        if (menuList && activeIndex != null) {
            const selector = `.menu-li:nth-child(${activeIndex + 1}) a,
                .menu-li:nth-child(${activeIndex + 1}) button`;
            const itemToFocus = menuList.querySelector(selector);
            if (itemToFocus != null) {
                (itemToFocus as HTMLAnchorElement).focus();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [activeIndex]);

    /**
     * Using useEffect here as it allows the focus handlers & blur handlers to fire for every element
     * before closing the element
     */
    useEffect(() => {
        if (internalOpenState === PopoutMenuInternalOpenState.OPENING) {
            setInternalOpenState(PopoutMenuInternalOpenState.IDLE);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
            onOpen != null && onOpen();
        } else if (internalOpenState === PopoutMenuInternalOpenState.CLOSING) {
            setInternalOpenState(PopoutMenuInternalOpenState.IDLE);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
            onClose != null && onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [internalOpenState]);

    /**
     * Handles re-setting internal state when the isOpen prop changes
     */
    useEffect(() => {
        if (isOpen) {
            setInternalOpenState(PopoutMenuInternalOpenState.IDLE);
        } else {
            setActiveIndex(null);
            setInternalOpenState(PopoutMenuInternalOpenState.IDLE);
        }
    }, [isOpen]);

    /**
     * Generates an SVG path that makes it easier to keep the component hovered over
     * when moving from the triggerElement to a menu item.
     *
     * NOTE: Set a fill on the path to see the shape it generates
     *
     * Useful links:
     * Method https://youtu.be/o0NtjY17v5w?t=1231
     * SVG path syntax https://css-tricks.com/svg-path-syntax-illustrated-guide/
     */
    useEffect(() => {
        if (isOpen && triggerRef.current && menuWrapperRef.current) {
            const triggerBBox = triggerRef.current.getBoundingClientRect();
            const menuBBox = menuWrapperRef.current.getBoundingClientRect();

            setSvgPath(`
                M 0,${triggerBBox.height}
                Q ${menuBBox.width - triggerBBox.width} ${triggerBBox.height},
                  ${menuBBox.width - triggerBBox.width} 0
                L ${menuBBox.width - triggerBBox.width} ${triggerBBox.height}
                Z
            `);
        }
    }, [isOpen]);

    if (menuTitle != null) {
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        navProps['aria-labelledby'] = menuTitleID;
    }

    const menuWrapperTransitions = useTransition(isOpen, {
        from: {
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            opacity: 0,
        },
    });

    /**
     * Renders a link for the menu. Depending on the properties of the link
     * It will either be a NextJS (WrappedLink) link or a regular one
     *
     * @param url
     * @param templatePath
     * @param onClick
     * @param text
     */
    function renderLink({
        url,
        templatePath,
        onClick,
        text,
    }: PopoutMenuItem): ReactNode {
        const commonProps = {
            tabIndex: -1,
            className: 'menu-link',
            linkColor: LinkColor.NAVIGATION,
            size: LinkSize.SMALL,
            onClick,
        };

        return url ? (
            <WrappedLink as={url} href={templatePath || url} {...commonProps}>
                {text}
            </WrappedLink>
        ) : (
            <Link href={url} {...commonProps}>
                {text}
            </Link>
        );
    }

    return (
        <StyledPopoutMenu
            trigger={trigger}
            menuItems={menuItems}
            isOpen={isOpen}
            idPrefix={idPrefix}
            menuTitle={menuTitle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            ref={rootRef}
            {...otherProps}
        >
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {isOpen && (
                <svg className="menu-trigger-path">
                    <path fill="none" d={svgPath} />
                </svg>
            )}

            {trigger(menuWrapperID, triggerRef)}

            {menuWrapperTransitions((props, item) =>
                item ? (
                    <animated.div
                        className="menu-wrapper"
                        id={menuWrapperID}
                        ref={menuWrapperRef}
                        style={props}
                    >
                        <nav {...navProps}>
                            {menuTitle != null && (
                                <p id={menuTitleID} className="menu-title">
                                    {menuTitle}
                                </p>
                            )}
                            <ul
                                className="menu-ul"
                                role="menu"
                                id={menuListID}
                                data-testid="popout-menu-ul"
                            >
                                {menuItems.map((link, index) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
                                        <li className="menu-li" key={index}>
                                            {renderLink(link)}
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>
                    </animated.div>
                ) : null
            )}
        </StyledPopoutMenu>
    );
};
